import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { Button, SkeletonList } from '../../../components/ui'
import { getApiErrorMessage } from '../../../utils/apiError'
import { getLegalDocument } from '../api/legalDocumentApi'
import PrivacyPolicyDocument from './PrivacyPolicyDocument'

export default function LegalDocumentDialog({ legalDocumentId, onClose }) {
  const closeButtonRef = useRef(null)
  const [document, setDocument] = useState(null)
  const [error, setError] = useState('')
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    closeButtonRef.current?.focus()
    const handleKeyDown = event => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  useEffect(() => {
    let active = true
    setDocument(null)
    setError('')

    getLegalDocument(legalDocumentId)
      .then(result => {
        if (active) setDocument(result.data)
      })
      .catch(requestError => {
        if (active) {
          setError(getApiErrorMessage(
            requestError,
            '약관 및 정책 문서를 불러오지 못했어요.',
          ))
        }
      })

    return () => { active = false }
  }, [legalDocumentId, reloadKey])

  return (
    <DialogBackdrop onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <DialogPanel
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-document-dialog-title"
      >
        <DialogHeader>
          <div>
            <small>{document ? `버전 ${document.version}` : '약관 및 정책 문서'}</small>
            <h2 id="legal-document-dialog-title">{document?.title || '문서를 불러오고 있어요'}</h2>
          </div>
          <DialogCloseButton
            ref={closeButtonRef}
            type="button"
            aria-label="상세 안내 닫기"
            onClick={onClose}
          >
            <CloseRoundedIcon aria-hidden="true" />
          </DialogCloseButton>
        </DialogHeader>

        <DialogBody>
          {!document && !error && (
            <SkeletonList rows={7} label="약관 및 정책 문서를 불러오는 중" />
          )}
          {error && (
            <ErrorState role="alert">
              <p>{error}</p>
              <Button $variant="secondary" onClick={() => setReloadKey(key => key + 1)}>
                다시 시도
              </Button>
            </ErrorState>
          )}
          {document && <PrivacyPolicyDocument content={document.content || ''} />}
        </DialogBody>
      </DialogPanel>
    </DialogBackdrop>
  )
}

const DialogBackdrop = styled.div`
  position: fixed;
  z-index: 120;
  inset: 0;
  display: grid;
  padding: 24px;
  background: rgba(17, 27, 64, 0.42);
  place-items: center;

  @media (max-width: 640px) { padding: 0; }
`

const DialogPanel = styled.section`
  display: grid;
  width: min(760px, 100%);
  max-height: min(820px, calc(100dvh - 48px));
  grid-template-rows: auto minmax(0, 1fr);
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.large};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadow.medium};

  @media (max-width: 640px) {
    width: 100%;
    max-height: 100dvh;
    height: 100dvh;
    border: 0;
    border-radius: 0;
  }
`

const DialogHeader = styled.header`
  display: flex;
  min-height: 76px;
  padding: 16px 20px;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  small { color: ${({ theme }) => theme.colors.textMuted}; font-size: 0.7rem; }
  h2 { margin: 4px 0 0; font-size: 1.05rem; }
`

const DialogCloseButton = styled.button`
  display: grid;
  width: 38px;
  height: 38px;
  flex: 0 0 auto;
  padding: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.surface};
  place-items: center;

  &:hover { color: ${({ theme }) => theme.colors.text}; background: ${({ theme }) => theme.colors.lightestGray}; }
`

const DialogBody = styled.div`
  min-height: 0;
  padding: 24px;
  overflow-y: auto;

  @media (max-width: 640px) { padding: 20px 18px 40px; }
`

const ErrorState = styled.div`
  display: grid;
  min-height: 240px;
  align-content: center;
  justify-items: center;
  gap: 14px;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;

  p { margin: 0; }
`
